import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from 'api/config';
import { useMutationHTTPRequest } from 'api/hooks/useHTTPRequest';
import { logError } from 'lib/sentry/logError';

import { Messages } from 'features/chat/config/types/Message';
import { User } from 'features/user/User';

import { showErrorToast } from 'shared/components/Toast';

export const useDeleteChatMessageReaction = ({
  chatId,
  user,
}: {
  chatId: string;
  user: User;
}) => {
  const { request } = useMutationHTTPRequest();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ reaction, messageId }: MutationFnPropType) =>
      await request({
        url: `/api/chats/${chatId}/messages/${messageId}/reactions/delete`,
        body: { reaction },
      }),
    {
      onMutate: async ({ reaction, messageId }: MutationFnPropType) => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.chatMessagesHistory, chatId],
          exact: false,
        });

        const previousMessagesMap = new Map();
        queryClient
          .getQueryCache()
          .findAll({
            queryKey: [queryKeys.chatMessagesHistory, chatId],
            exact: false,
          })
          .forEach((query) => {
            previousMessagesMap.set(
              JSON.stringify(query.queryKey),
              query.state.data
            );
          });

        queryClient
          .getQueryCache()
          .findAll({
            queryKey: [queryKeys.chatMessagesHistory, chatId],
            exact: false,
          })
          .forEach((query) => {
            queryClient.setQueryData<{ messages: Messages }>(
              query.queryKey,
              (old) => {
                if (!old?.messages) return old;

                return {
                  ...old,
                  messages: old.messages.map((message) => {
                    if (message.messageId === messageId) {
                      const updatedReactions = { ...message.reactions };

                      if (updatedReactions[reaction]) {
                        updatedReactions[reaction] = updatedReactions[
                          reaction
                        ].filter(
                          (r) =>
                            r.email !== user.email &&
                            r.firstName !== user.firstName &&
                            r.lastName !== user.lastName
                        );

                        if (updatedReactions[reaction].length === 0) {
                          delete updatedReactions[reaction];
                        }
                      }

                      return {
                        ...message,
                        reactions: updatedReactions,
                      };
                    }
                    return message;
                  }),
                };
              }
            );
          });

        return { previousMessagesMap };
      },
      onError: (err, { messageId }, context) => {
        showErrorToast(
          "There's an error with deleting reaction. Please try again later."
        );

        if (context?.previousMessagesMap) {
          context.previousMessagesMap.forEach((data, key) => {
            queryClient.setQueryData(JSON.parse(key), data);
          });
        }

        logError(err);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.chatMessagesHistory, chatId],
          exact: false,
        });
      },
    }
  );
};

type MutationFnPropType = { reaction: string; messageId: string };
